/* eslint-disable complexity */
import React, { useState, useContext } from 'react';
import './HotDealVouchers.css';
import PropTypes from 'prop-types';
import VouchersDisplay from '../Vouchers/VouchersDisplay.jsx';
import Voucher from '../Voucher/Voucher.jsx';
import Slider from '../Slider/Slider.jsx';
import ProductVoucher from '../ProductVoucher/ProductVoucher.jsx';
import AddToCartButtons from '../AddToCart/AddToCartButtons.jsx';
import { CartContext } from '../../services/context/CartContext.js';
import { CategoriesContext, CategoriesCompleteContext } from '../../services/context/CategoriesContext.js';
import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';
import CheckoutButton from '../CheckoutButton/CheckoutButton.jsx';
import moment from 'moment/moment.js';
import { home, hotDealsPageCartEvent, homePageCartEvent, hotDealsSliderCartEvent, homePageSliderCartEvent } from '../../services/Constants/config.js';

/**
 * @param {object} categories categories object
 * @returns {Array} flat list of all vouchers linked to any campaign
 */
function getAllCampaignVouchers(categories) {
  return categories.reduce((mergedVendors, category) => [
    ...mergedVendors,
    ...category.vendors,
  ], []).reduce((mergedVouchers, nextVendor) => {
    const vouchersWithVendorInfo = nextVendor.vouchers.map(v => ({
      ...v,
      vendorName: nextVendor.name,
      vendorIconUrl: nextVendor.iconUrl,
    }));
    return [
      ...mergedVouchers,
      ...vouchersWithVendorInfo,
    ];
  }, []).filter(voucher => Boolean(voucher.campaign));
}

/**
 * @param {object} props - component props
 * @param {Array<string>} [props.campaignIds] - list of campaign Id voucher to filter vouchers
 * @param {number} [props.maxSize] - number of vouchers to display
 * @param {JSX.Element} props.onEmpty - element to show when there are no campaign vouchers
 * @param {string} props.pageLabel - optional fromPage string variable
 * @returns {JSX.Element} HotDealVouchers component
 */
export default function HotDealVouchers({ campaignIds = [], maxSize, onEmpty, pageLabel }) {
  const cart = useContext(CartContext);
  const categories = useContext(CategoriesContext);
  const categoriesComplete = useContext(CategoriesCompleteContext);
  const MoneyFormatter = useContext(MoneyFormatterContext);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  let campaignVouchers = null;
  const pageLabelForAnalytics = pageLabel === home ? homePageSliderCartEvent : hotDealsSliderCartEvent;

  if (!campaignVouchers && categories && categoriesComplete) {
    campaignVouchers = getAllCampaignVouchers(categories).filter(voucher => voucher.campaign && campaignIds.includes((voucher.campaign).toString()));

    campaignVouchers = campaignVouchers.filter(voucher => moment().isBetween(voucher.campaignStartDate, voucher.campaignEndDate));

    if (maxSize > 0 && maxSize < campaignVouchers.length) {
      campaignVouchers = campaignVouchers.slice(0, maxSize);
    }
  }

  const handleVoucherClick = voucher => {
    setSelectedVoucher(voucher);
  };

  const skeletonVouchers = Array.from({ length: 2 }, (_, key) => <Voucher key={ key } />);

  return (
    <div className={ `hot-deals-voucher-container ${!campaignVouchers ? 'empty skeleton-loader' : ''}` }>
      {
        <VouchersDisplay
          vouchers={
            (campaignVouchers || []).map(campaignVoucher => (
              <Voucher
                key={ campaignVoucher.id }
                iconUrl={ campaignVoucher.vendorIconUrl }
                onClick={ () => handleVoucherClick(campaignVoucher) }
                title={
                  <>
                    <div className='hotdeal-vendorName'>
                      {campaignVoucher.vendorName}
                    </div>
                    <div className='pay-get'>
                      {`Pay ${MoneyFormatter.format(MoneyFormatter.getValue(campaignVoucher.cost))} get `}
                      <em>
                        {MoneyFormatter.format(MoneyFormatter.getValue(campaignVoucher.options.buyNow.value))}
                      </em>
                    </div>
                  </>
                }
                addedValue={
                  `Get ${MoneyFormatter.format(MoneyFormatter.subtract(
                    MoneyFormatter.getValue(campaignVoucher.cost),
                    MoneyFormatter.getValue(campaignVoucher.options.buyNow.value),
                  ))} extra`
                }
                actionItem={
                  <AddToCartButtons
                    voucher={ campaignVoucher }
                    pageLabel={ pageLabel === home ? homePageCartEvent : hotDealsPageCartEvent }
                  />
                }
              />
            ))
          }
          isLoading={ !campaignVouchers }
          onLoading={ skeletonVouchers }
          onEmpty={ onEmpty }
        />
      }

      {
        selectedVoucher && (
          <Slider
            slot={
              <ProductVoucher
                voucher={ selectedVoucher }
                actionItem={
                  <AddToCartButtons
                    voucher={ selectedVoucher }
                    pageLabel={ pageLabelForAnalytics }
                  />
                }
                descriptionType='buyNow'
                pageLabel={ pageLabelForAnalytics }
              />
            }
            onClickOutside={ () => setSelectedVoucher(null) }
          />
        )
      }

      { cart.length > 0 && <CheckoutButton /> }
    </div>
  );
}

HotDealVouchers.propTypes = {
  vouchers: PropTypes.element,
  campaignIds: PropTypes.arrayOf(PropTypes.string),
  maxSize: PropTypes.number,
  onEmpty: PropTypes.element,
  pageLabel: PropTypes.string,
};
